/**
 * Inbound Email entity
 *
 * An email received through the Mailgun inbound pipeline for a school. Kept in
 * sync with `apps/backend/prisma/schema/content.prisma` (`InboundEmail` model /
 * `InboundEmailStatus` enum). This is the client-facing (web dashboard) shape:
 * IDs are public CUIDs and timestamps are ISO strings.
 */

import { z } from 'zod'

export const InboundEmailStatusSchema = z.enum(['PENDING', 'PROCESSING', 'PROCESSED', 'FAILED'])
export type InboundEmailStatus = z.infer<typeof InboundEmailStatusSchema>

/** Reference to the content record an inbound email was converted into. */
export const InboundEmailContentRefSchema = z
  .object({
    id: z.string()
  })
  .nullable()

export const InboundEmailSchema = z.object({
  id: z.string(),
  messageId: z.string().nullable(),
  sender: z.string(),
  recipient: z.string(),
  subject: z.string().nullable(),
  /** RFC-5322 display From header (may differ from `sender`). */
  fromHeader: z.string().nullable(),
  /** When the email was received (from the Mailgun payload). */
  timestamp: z.string().nullable(),

  status: InboundEmailStatusSchema,
  processingAttempts: z.number(),
  lastProcessingError: z.string().nullable(),
  processedAt: z.string().nullable(),

  /** Original, unmodified content as received from Mailgun. */
  bodyHtmlRaw: z.string().nullable(),
  bodyPlainRaw: z.string().nullable(),
  /** Cleaned / stripped versions the converter works from. */
  bodyHtml: z.string().nullable(),
  bodyPlain: z.string().nullable(),
  /** Raw Mailgun `message-headers` (array of [name, value] pairs). */
  messageHeaders: z.any().nullable(),
  /** Attachment metadata (bytes live on Cloudinary, not here). */
  attachments: z.any().nullable(),

  emailCustomId: z.string().nullable(),
  /** The content record this email became, if conversion succeeded. */
  contentRecord: InboundEmailContentRefSchema,

  createdAt: z.string(),
  updatedAt: z.string().nullable()
})
export type InboundEmail = z.infer<typeof InboundEmailSchema>

export const InboundEmailListResponseSchema = z.object({
  data: z.array(InboundEmailSchema),
  meta: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number()
  }),
  message: z.string()
})
export type InboundEmailListResponse = z.infer<typeof InboundEmailListResponseSchema>
